/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LinearProgress, Fade, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme, { buildLightTheme } from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark, { buildDarkTheme } from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import { useAuth } from "layouts/authentication/auth/auth";
import ProtectedRoute from "layouts/authentication/auth/protected-route";

// Material Dashboard 2 React routes
import routes, { getFilteredRoutes } from "routes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  setSidenavHidden,
  setSidenavMode,
} from "context";
import SidenavRevealStrip from "examples/Sidenav/SidenavRevealStrip";
import { ManagementUnitProvider } from "context/ManagementUnitContext";
import DialogProvider from "components/Dialogs/DialogProvider";

// Images
import brandWhite from "assets/images/renix-logo-512x512-white-bg.png";
import brandDark from "assets/images/renix-logo-512x512.png";
import brandWhiteTransparent from "assets/images/renix-logo-white-transparent.png";

import LandingPage from "layouts/landing";
import UyelikHome from "layouts/uyelik/home";

// Loading Component
function LoadingScreen({ darkMode }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(timer);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    return () => clearInterval(timer);
  }, []);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100vw"
        height="100vh"
        flexDirection="column"
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <Fade in={true}>
          <MDBox textAlign="center">
            <MDBox mb={3}>
              <img
                src={darkMode ? brandWhite : brandDark}
                alt="Renix Logo"
                style={{
                  width: "150px",
                  height: "auto",
                }}
              />
            </MDBox>

            <MDBox width="300px" mb={2}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: (theme) =>
                    darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: (theme) => theme.palette.info.main,
                  },
                }}
              />
            </MDBox>

            <MDBox fontSize="2rem" fontWeight="bold" color={darkMode ? "white" : "dark"}>
              {progress}%
            </MDBox>

            <MDBox fontSize="0.875rem" color="text.secondary" mt={1}>
              Renix Mülk Yönetimi Yükleniyor
            </MDBox>
          </MDBox>
        </Fade>
      </MDBox>
    </ThemeProvider>
  );
}

export default function App() {
  // Menü ve yönlendirme rol bayraklarını vekil bağlamında malikin görünümünden alır.
  const { user, loading, rolKullanicisi } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavHidden,
    sidenavMode,
    themePreset,
  } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  // The two sidenav thresholds (see assets/theme/base/breakpoints.js for the derivation).
  const isDocked = useMediaQuery(theme.breakpoints.up("navOverlay")); // >=1024: beside the content
  const fitsFullNav = useMediaQuery(theme.breakpoints.up("navFull")); // >=1440: full menu affordable

  // Brand logo: white variant only on dark + non-white sidenav; dark logo otherwise
  // (default sidenav is now a light surface).
  const brandLogo = darkMode && !whiteSidenav ? brandWhiteTransparent : brandDark;

  const handleCycleMode = () => {
    const next = sidenavMode === "auto" ? "pinned" : sidenavMode === "pinned" ? "hidden" : "auto";
    setSidenavMode(dispatch, next);
    localStorage.setItem("sidenavMode", next);
  };

  // Derive mini/hidden from mode + breakpoint.
  //
  // Below navOverlay (<1024px) the drawer is an off-canvas overlay (SidenavRoot translates it out
  // and DashboardLayout stops reserving margin), so the only sane start state is closed and the
  // navbar hamburger is the way in. Two consequences that the mode switch must respect there:
  //   - "pinned" must NOT force it open, or a permanent 224px drawer overlaps the page with no
  //     dismiss affordance (variant="permanent" has no backdrop);
  //   - "hidden" must NOT unmount it, or there is no navigation at all — the reveal strip is
  //     itself navOverlay-and-up only.
  useEffect(() => {
    if (!isDocked) {
      setSidenavHidden(dispatch, false);
      setMiniSidenav(dispatch, true);
      return;
    }

    if (sidenavMode === "hidden") {
      setSidenavHidden(dispatch, true);
      return;
    }

    setSidenavHidden(dispatch, false);
    // pinned = always full; auto = full where it fits beside the content (>=1440px), rail below.
    setMiniSidenav(dispatch, sidenavMode === "pinned" ? false : !fitsFullNav);
  }, [sidenavMode, fitsFullNav, isDocked, dispatch]);

  // Kullanıcı rolüne göre filtrelenmiş route'ları al
  const filteredRoutes = useMemo(() => getFilteredRoutes(rolKullanicisi), [rolKullanicisi]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return <Route path={route.route} element={route.component} key={route.key} />;
        }

        return null;
      })
      .flat(); // Flatten the array to handle nested routes

  // Determine default redirect based on authentication status and role
  const getDefaultRedirect = () => {
    const rol = rolKullanicisi;
    const vekilMi = Boolean(rol && rol.vekaletler && rol.vekaletler.length);
    if (rol && (rol.admin || rol.kiraci || rol.usta || rol.ev_sahibi || vekilMi)) {
      return <Navigate to="/dashboard" />;
    }
    if (rol && rol.emlakci) {
      return <Navigate to="/emlakci/ilan" />;
    }
    if (rol && rol.uyelik) {
      return <UyelikHome />;
    }
    return <LandingPage />;
  };

  // Check if current route is authentication route
  const isAuthRoute = pathname.startsWith("/authentication/");

  // Public pages always use the default light theme regardless of user's dark/preset settings
  const isPublicPath = useMemo(
    () =>
      isAuthRoute ||
      pathname === "/" ||
      ["/hakkimizda", "/hizmetlerimiz", "/blog", "/ilanlar"].includes(pathname) ||
      // Emlak ofisi public sayfalari: sahibinden'e verilecek URL'ler. Giris
      // yapmis kullanici da public temayla gormeli, panele donmemeli.
      pathname === "/emlak-ofisleri" ||
      pathname.startsWith("/emlak-ofisi/") ||
      pathname.startsWith("/emlak-ofisi-basvuru") ||
      pathname === "/emlak-ofisi-girisi" ||
      pathname.startsWith("/blog-detail") ||
      pathname.startsWith("/ilan-detay") ||
      pathname.startsWith("/terms-of-service") ||
      pathname.startsWith("/kvkk") ||
      pathname.startsWith("/privacy-policy") ||
      pathname.startsWith("/cookie-policy") ||
      pathname.startsWith("/sizi-arayalim"),
    [pathname, isAuthRoute]
  );

  // Kimlik bilgisini admin urettiyse ilk giriste degisim zorunlu (emlak ofisi
  // yetkilisi akisi, S4 karari). Sunucu bayragi /auth/status ile geliyor.
  // Public sayfalar (isAuthRoute dahil) kapsam disi: kilit uygulamanin ic
  // kismini korur, ziyaretcinin de gorebildigi sayfalari kapatmaz.
  const sifreDegisimiZorunlu =
    Boolean(user?.sifre_degistirmeli) &&
    !isPublicPath &&
    pathname !== "/settings/change-credentials";

  const routesElement = sifreDegisimiZorunlu ? (
    <Routes>
      <Route path="*" element={<Navigate to="/settings/change-credentials" replace />} />
    </Routes>
  ) : (
    <Routes>
      {getRoutes(filteredRoutes)}
      <Route path="/" element={getDefaultRedirect()} />
      <Route path="*" element={getDefaultRedirect()} />
    </Routes>
  );

  // Show loading screen while auth status is being checked.
  // Without this, the app renders LandingPage (user=null) then flickers
  // to the real UI when /auth/status responds.
  if (loading) {
    return <LoadingScreen darkMode={darkMode} />;
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <ManagementUnitProvider>
          <DialogProvider>
            <CssBaseline />
            {layout === "dashboard" && !isAuthRoute && user && (
              <>
                {!sidenavHidden ? (
                  <Sidenav
                    color={sidenavColor}
                    brand={brandLogo}
                    brandName="Renix Mulk Yonetimi"
                    routes={filteredRoutes}
                    sidenavMode={sidenavMode}
                    onCycleMode={handleCycleMode}
                  />
                ) : (
                  <SidenavRevealStrip onReveal={handleCycleMode} />
                )}
                <Configurator />
              </>
            )}
            {layout === "vr" && <Configurator />}
            <Suspense fallback={<LoadingScreen darkMode={darkMode} />}>{routesElement}</Suspense>
          </DialogProvider>
        </ManagementUnitProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider
      theme={
        isPublicPath && !user
          ? buildLightTheme("renix")
          : darkMode
          ? buildDarkTheme(themePreset)
          : buildLightTheme(themePreset)
      }
    >
      <ManagementUnitProvider>
        <DialogProvider>
          <CssBaseline />
          {layout === "dashboard" && !isAuthRoute && pathname !== "/" && user && (
            <>
              {!sidenavHidden ? (
                <Sidenav
                  color={sidenavColor}
                  brand={brandLogo}
                  brandName="Renix Mulk Yonetimi"
                  routes={filteredRoutes}
                  sidenavMode={sidenavMode}
                  onCycleMode={handleCycleMode}
                />
              ) : (
                <SidenavRevealStrip onReveal={handleCycleMode} />
              )}
              <Configurator />
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Suspense fallback={<LoadingScreen darkMode={darkMode} />}>{routesElement}</Suspense>
        </DialogProvider>
      </ManagementUnitProvider>
    </ThemeProvider>
  );
}
